
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import { extractErrorCode, resolveErrorMessageKey } from "@root/core/utilities";
import { ecpayErrorMessageMap, ecpayFallbackKey } from "@/helpers/error-code-ecpay";
import appConfig from "@/config/default/application";
import {
    promoValidateErrorMessageMap,
    promoValidateFallbackKey,
} from "@/helpers/error-code";

@Component
export default class TranslationJobTopupModal extends Vue {
    get topupMinimumAmount() {
        return appConfig.topupMinimumAmount;
    }

    @Prop({ type: Boolean, default: false })
    visible!: boolean;

    @Prop({ type: String, default: "-" })
    targetOrgName!: string;

    @Prop({ type: String, default: null })
    organizationId!: string | null;

    @Emit("succeed")
    emitSucceed(payload: { amount: number; order: any }) {
        return payload;
    }

    @Emit("failed")
    emitFailed(payload: { error: any; code: string }) {
        return payload;
    }

    activeTab: "online" | "redeem" = "online";
    selectedPreset: number | null = 300;
    customAmount: number | null = null;
    discountCode = "";
    discountRate = 0;
    discountAmountOverride: number | null = null;
    discountFeedback = "";
    discountFeedbackClass = "";
    redeemCode = "";
    invoiceType: "PERSONAL" | "COMPANY" | "OVERSEAS" | null = null;
    invoiceEmail = "";
    invoiceCarrierNum = "";
    invoiceCompanyName = "";
    invoiceTaxId = "";
    agreeTerms = false;
    isProcessing = false;
    private ecpaySource: EventSource | null = null;
    private ecpayTimeoutTimer: number | null = null;
    private pendingEcpayResult: { status: "SUCCESS" | "FAILED"; payload: any } | null =
        null;
    private readonly onVisibilityChange = () => {
        this.flushPendingEcpay();
    };

    private readonly onWindowFocus = () => {
        this.flushPendingEcpay();
    };

    get canPay() {
        if (this.shouldShowTermsAgree && !this.agreeTerms) return false;
        return this.total > 0 && !this.invoiceError;
    }

    get shouldShowTermsAgree() {
        return Boolean(this.invoiceType);
    }

    get invoiceError() {
        if (!this.invoiceType) {
            return this.$_("w_InvoiceTypeRequired");
        }
        if (!this.invoiceEmail) {
            return this.$_("w_InvoiceEmailRequired");
        }
        if (this.invoiceType === "COMPANY") {
            if (!this.invoiceCompanyName) {
                return this.$_("w_InvoiceCompanyNameRequired");
            }
            const taxIdError = this.getTaxIdErrorMessage(this.invoiceTaxId || "");
            if (taxIdError) return taxIdError;
        }
        return "";
    }

    get invoiceErrorEmail() {
        if (!this.invoiceType) return "";
        if (!this.invoiceEmail) return this.$_("w_InvoiceEmailRequired");
        return "";
    }

    get invoiceErrorCompanyName() {
        if (this.invoiceType !== "COMPANY") return "";
        if (!this.invoiceCompanyName) return this.$_("w_InvoiceCompanyNameRequired");
        return "";
    }

    get invoiceErrorTaxId() {
        if (this.invoiceType !== "COMPANY") return "";
        return this.getTaxIdErrorMessage(this.invoiceTaxId || "");
    }

    get visibleProxy() {
        return this.visible;
    }
    set visibleProxy(value: boolean) {
        this.$emit("update:visible", value);
    }

    get subtotal() {
        const custom =
            this.customAmount && this.customAmount > 0 ? this.customAmount : null;
        const value = custom ?? this.selectedPreset ?? 0;
        return Math.round(value);
    }

    get discountAmount() {
        if (this.discountAmountOverride != null) {
            return Math.round(this.discountAmountOverride);
        }
        return Math.round(this.subtotal * this.discountRate);
    }

    get total() {
        return Math.max(0, this.subtotal - this.discountAmount);
    }

    formatTwd(value: number) {
        return new Intl.NumberFormat("zh-TW", {
            style: "currency",
            currency: "TWD",
            maximumFractionDigits: 0,
        }).format(value);
    }

    formatPoints(value: number) {
        if (!Number.isFinite(value)) return "-";
        return `${Math.ceil(value).toLocaleString()} ${this.$_("w_PointUnit")}`;
    }

    private resetDiscountState() {
        this.discountCode = "";
        this.discountRate = 0;
        this.discountAmountOverride = null;
        this.discountFeedback = "";
        this.discountFeedbackClass = "";
    }

    selectPreset(amount: number) {
        this.selectedPreset = amount;
        this.customAmount = null;
        this.resetDiscountState();
    }

    async applyDiscount() {
        const code = this.discountCode.trim().toUpperCase();
        if (!code) {
            this.discountRate = 0;
            this.discountAmountOverride = null;
            this.discountFeedback = "";
            this.discountFeedbackClass = "";
            return;
        }

        try {
            const result = await (this as any).$server.C("promotion-code/validate", {
                code,
                organizationId: this.organizationId ?? undefined,
                amount: this.subtotal,
            });
            const promoKind = String(result?.kind ?? "DISCOUNT");
            const minSpend = Number(result?.minSpendAmount ?? 0);
            if (promoKind === "CREDIT") {
                this.discountRate = 0;
                this.discountAmountOverride = 0;
                this.discountFeedback = this.$_("w_TranslationJobTopupDiscountApplied");
            } else {
                const discountType = String(result?.discountType ?? "PERCENT");
                if (discountType === "AMOUNT") {
                    const amountOff = Number(result?.amountOff ?? 0);
                    this.discountRate = 0;
                    this.discountAmountOverride = Math.min(amountOff, this.subtotal);
                    this.discountFeedback = this.$_("w_TranslationJobTopupDiscountApplied");
                } else {
                    const percentOff = Number(result?.percentOff ?? 0);
                    this.discountRate = Math.max(0, percentOff / 100);
                    this.discountAmountOverride = null;
                    this.discountFeedback = this.$_("w_TranslationJobTopupDiscountApplied");
                }
            }
            this.discountFeedbackClass = "text-success";
        } catch (error) {
            const rawError = (error as any)?.body ?? error;
            const code =
                extractErrorCode(rawError) ??
                rawError?.error?.code ??
                (error instanceof Error ? error.message : "");
            this.discountRate = 0;
            this.discountAmountOverride = null;
            if (code === "PROMO_CODE_REQUIRED") {
                this.discountFeedback = "";
                this.discountFeedbackClass = "";
                if (error && typeof error === "object") {
                    (error as any).handled = true;
                }
                return;
            }
            const messageKey = resolveErrorMessageKey(
                code,
                promoValidateErrorMessageMap,
                promoValidateFallbackKey,
            );
            if (code === "PROMO_MIN_SPEND") {
                const minSpend = Number(
                    rawError?.error?.minSpendAmount ?? rawError?.minSpendAmount ?? 0,
                );
                this.discountFeedback = this.$_(messageKey, [
                    minSpend > 0 ? this.formatTwd(minSpend) : "-",
                ]);
            } else {
                this.discountFeedback = this.$_(messageKey);
            }
            this.discountFeedbackClass = "text-danger";
            if (error && typeof error === "object") {
                (error as any).handled = true;
            }
        }
    }

    async submitPay() {
        if (this.isProcessing) return;
        this.isProcessing = true;
        try {
            const order = await this.createEcpayOrder(this.total);
            this.submitEcpayForm(order?.action, order?.fields);
            const result = await this.waitForEcpayResult(order?.merchantTradeNo);
            this.handleEcpayResult(result, order);
        } catch (error) {
            const code =
                extractErrorCode((error as any)?.body ?? error) ??
                (error instanceof Error ? error.message : "");
            this.handleEcpayError(error, code);
        } finally {
            this.isProcessing = false;
        }
    }

    @Watch("visible")
    async onVisibleChange(value: boolean) {
        if (!value) {
            this.stopEcpaySse();
            this.isProcessing = false;
            this.pendingEcpayResult = null;
            this.removeVisibilityListener();
            return;
        }
        this.stopEcpaySse();
        this.activeTab = "online";
        this.selectedPreset = 300;
        this.customAmount = null;
        this.resetDiscountState();
        this.redeemCode = "";
        this.resetInvoiceState();
        this.isProcessing = false;
        this.pendingEcpayResult = null;
        this.addVisibilityListener();
        this.flushPendingEcpay();
        await this.loadInvoiceDefaults();
    }

    @Watch("customAmount")
    onCustomAmountChange(value: number | null) {
        if (value == null || Number.isNaN(value) || value <= 0) return;
        const normalized = Math.max(this.topupMinimumAmount, Math.floor(value));
        if (normalized !== value) {
            this.customAmount = normalized;
        }
        this.selectedPreset = null;
        this.resetDiscountState();
    }

    private createEcpayOrder(amount: number) {
        const server = (this as any).$server;
        return server.C("ecpay/credit/create-order", {
            amount,
            creditAmount: this.subtotal,
            discountCode: this.discountCode?.trim() || undefined,
            organizationId: this.organizationId ?? undefined,
            invoiceType: this.invoiceType,
            invoiceEmail: this.invoiceEmail?.trim() || undefined,
            invoiceCarrierNum: this.invoiceCarrierNum?.trim() || undefined,
            invoiceCompanyName: this.invoiceCompanyName?.trim() || undefined,
            invoiceTaxId: this.invoiceTaxId?.trim() || undefined,
        });
    }

    private resetInvoiceState() {
        this.invoiceType = null;
        this.invoiceEmail = this.$user?.invoiceEmail ?? this.$user?.email ?? "";
        this.invoiceCarrierNum = this.$user?.invoiceCarrierNum ?? "";
        this.invoiceCompanyName = "";
        this.invoiceTaxId = "";
        this.agreeTerms = false;
    }

    private async loadInvoiceDefaults() {
        const server = (this as any).$server;
        if (this.$user?.id) {
            try {
                const userPayload = await server.R("user", {
                    where: { id: this.$user.id },
                    select: {
                        id: true,
                        invoiceType: true,
                        invoiceEmail: true,
                        invoiceCarrierNum: true,
                    },
                });
                const userResults =
                    userPayload?.results ??
                    userPayload?.data?.results ??
                    userPayload ??
                    [];
                const user = Array.isArray(userResults) ? userResults[0] : userResults;
                if (user) {
                    if (user.invoiceEmail) {
                        this.invoiceEmail = user.invoiceEmail;
                    }
                    if (user.invoiceCarrierNum) {
                        this.invoiceCarrierNum = user.invoiceCarrierNum;
                    }
                    if (!this.invoiceType && user.invoiceType) {
                        this.invoiceType = user.invoiceType;
                    }
                }
            } catch {
                // ignore
            }
        }
        if (!this.organizationId) return;
        try {
            const payload = await server.R("organization", {
                where: { id: this.organizationId },
                select: {
                    id: true,
                    invoiceType: true,
                    invoiceEmail: true,
                    invoiceCompanyName: true,
                    invoiceTaxId: true,
                },
            });
            const results = payload?.results ?? payload?.data?.results ?? payload ?? [];
            const org = Array.isArray(results) ? results[0] : results;
            if (!org) return;
            if (org.invoiceEmail) {
                this.invoiceEmail = org.invoiceEmail;
            }
            if (org.invoiceCompanyName) {
                this.invoiceCompanyName = org.invoiceCompanyName;
            }
            if (org.invoiceTaxId) {
                this.invoiceTaxId = org.invoiceTaxId;
            }
            if (!this.invoiceType) {
                const orgHasInvoice =
                    Boolean(org.invoiceEmail) ||
                    Boolean(org.invoiceCompanyName) ||
                    Boolean(org.invoiceTaxId);
                if (orgHasInvoice && org.invoiceType) {
                    this.invoiceType = org.invoiceType;
                } else {
                    const userHasInvoice =
                        Boolean(this.$user?.invoiceEmail) ||
                        Boolean(this.$user?.invoiceCarrierNum);
                    if (userHasInvoice) {
                        this.invoiceType = this.$user?.invoiceType ?? "PERSONAL";
                    }
                }
            }
        } catch {
            // ignore
        }
    }

    private submitEcpayForm(action: string, fields: Record<string, string>) {
        if (!action || !fields || typeof fields !== "object") {
            throw new Error("ECPAY_ORDER_INVALID");
        }
        const form = document.createElement("form");
        form.method = "POST";
        form.action = action;
        form.target = "_blank";
        form.acceptCharset = "UTF-8";
        form.style.display = "none";

        for (const [name, value] of Object.entries(fields)) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = String(value ?? "");
            form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
        form.remove();
    }

    private waitForEcpayResult(merchantTradeNo: string) {
        return new Promise<any>((resolve, reject) => {
            if (!merchantTradeNo) {
                reject(new Error("ECPAY_TRADE_NO_REQUIRED"));
                return;
            }

            const token = this.resolveAccessToken();
            if (!token) {
                reject(new Error("ECPAY_TOKEN_REQUIRED"));
                return;
            }

            const baseUrl = this.$server
                .Url("ecpay/credit/stream" as any)
                .replace(/\?$/, "");
            const endpoint = new URL(baseUrl, window.location.origin);
            endpoint.searchParams.set("merchantTradeNo", merchantTradeNo);
            if (this.organizationId) {
                endpoint.searchParams.set("organizationId", this.organizationId);
            }
            endpoint.searchParams.set("token", token);

            this.stopEcpaySse();
            this.ecpaySource = new EventSource(endpoint.toString());

            this.ecpaySource.addEventListener("payment", (event) => {
                try {
                    const payload = event?.data ? JSON.parse(event.data) : null;
                    this.stopEcpaySse();
                    resolve(payload);
                } catch (error) {
                    this.stopEcpaySse();
                    reject(error);
                }
            });

            this.ecpaySource.addEventListener("error", () => {
                this.stopEcpaySse();
                reject(new Error("ECPAY_STREAM_ERROR"));
            });

            this.ecpayTimeoutTimer = window.setTimeout(() => {
                this.stopEcpaySse();
                reject(new Error("ECPAY_TIMEOUT"));
            }, 10 * 60 * 1000);
        });
    }

    private handleEcpayResult(result: any, order: any) {
        if (result?.status === "SUCCESS") {
            const payload = { amount: this.subtotal, order };
            if (this.shouldDeferEcpayEmit()) {
                this.pendingEcpayResult = { status: "SUCCESS", payload };
                return;
            }
            this.emitSucceed(payload);
            return;
        }

        const code = String(result?.code ?? "ECPAY_FAILED");
        const payload = { error: result, code };
        if (this.shouldDeferEcpayEmit()) {
            this.pendingEcpayResult = { status: "FAILED", payload };
            return;
        }
        this.emitFailed(payload);
        if (!this.hasFailedListener()) {
            this.showDefaultFailedAlert(code);
            (result as any).handled = true;
        }
    }

    private handleEcpayError(error: any, code: string) {
        const payload = { error, code };
        if (this.shouldDeferEcpayEmit()) {
            this.pendingEcpayResult = { status: "FAILED", payload };
            return;
        }
        this.emitFailed(payload);
        if (!this.hasFailedListener()) {
            this.showDefaultFailedAlert(code);
            if (error && typeof error === "object") {
                (error as any).handled = true;
            }
        }
    }

    private shouldDeferEcpayEmit() {
        if (typeof document === "undefined") return false;
        return document.visibilityState !== "visible";
    }

    private addVisibilityListener() {
        if (typeof document === "undefined") return;
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        window.removeEventListener("focus", this.onWindowFocus);
        window.addEventListener("focus", this.onWindowFocus);
    }

    private removeVisibilityListener() {
        if (typeof document === "undefined") return;
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
        window.removeEventListener("focus", this.onWindowFocus);
    }

    private flushPendingEcpay() {
        if (!this.pendingEcpayResult) return;
        if (!this.visibleProxy) return;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") {
            return;
        }
        const pending = this.pendingEcpayResult;
        this.pendingEcpayResult = null;
        if (pending.status === "SUCCESS") {
            this.emitSucceed(pending.payload);
            return;
        }
        const code = String(pending.payload?.code ?? "ECPAY_FAILED");
        this.emitFailed(pending.payload);
        if (!this.hasFailedListener()) {
            this.showDefaultFailedAlert(code);
            if (pending.payload?.error && typeof pending.payload.error === "object") {
                (pending.payload.error as any).handled = true;
            }
        }
    }

    private stopEcpaySse() {
        if (this.ecpaySource) {
            this.ecpaySource.close();
            this.ecpaySource = null;
        }
        if (this.ecpayTimeoutTimer) {
            window.clearTimeout(this.ecpayTimeoutTimer);
            this.ecpayTimeoutTimer = null;
        }
    }

    private resolveAccessToken() {
        const headers = this.$server.applyAccessToken?.() ?? {};
        const rawAuth = headers.Authorization ?? "";
        const token = String(rawAuth).replace(/^Bearer\\s+/i, "");
        return token || null;
    }

    private isValidTaxId(input: string) {
        if (!/^\d{8}$/.test(input)) return false;
        const weights = [1, 2, 1, 2, 1, 2, 4, 1];
        const sum = input
            .split("")
            .map((digit, index) => Number(digit) * weights[index])
            .map((value) => Math.floor(value / 10) + (value % 10))
            .reduce((acc, value) => acc + value, 0);
        if (sum % 10 === 0) return true;
        return input[6] === "7" && (sum + 1) % 10 === 0;
    }

    private getTaxIdErrorMessage(input: string) {
        if (!/^\d{8}$/.test(input)) return this.$_("w_InvoiceTaxIdInvalid");
        if (!this.isValidTaxId(input)) return this.$_("w_InvoiceTaxIdInvalidChecksum");
        return "";
    }

    private hasFailedListener() {
        const listeners = (this.$listeners as any)?.failed;
        if (Array.isArray(listeners)) return listeners.length > 0;
        return Boolean(listeners);
    }

    private showDefaultFailedAlert(code: string) {
        const messageKey = resolveErrorMessageKey(
            code,
            ecpayErrorMessageMap,
            ecpayFallbackKey,
        );
        this.$alert?.(
            {
                title: this.$_("w_Error"),
                message: this.$_(messageKey),
            },
            "danger",
        );
    }
}
